import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Estatus del proyecto con su % de avance (fuente de verdad del avance de obra:
 * `proyectos.id_estatus_proyecto → estatus_proyecto.porcentaje_avance`).
 * Chip con anillo de progreso + "Planta Baja · 46%"; al hover/focus muestra la
 * escalera de etapas del catálogo (completadas, actual y siguientes).
 */

export interface EtapaCatalogo {
  id: number;
  nombre: string;
  porcentaje: number | null;
}

/** Catálogo activo de `estatus_proyecto` ordenado por avance. Se carga una vez. */
export function useEstatusProyectoCatalogo(enabled = true) {
  return useQuery({
    queryKey: ["estatus-proyecto", "catalogo-avance"],
    enabled,
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    retry: false,
    queryFn: async (): Promise<EtapaCatalogo[]> => {
      const { data, error } = await supabase
        .from("estatus_proyecto")
        .select("id, nombre, porcentaje_avance")
        .eq("activo", true);
      if (error) throw error;
      return (data ?? [])
        .map((r) => ({ id: r.id, nombre: r.nombre, porcentaje: r.porcentaje_avance ?? null }))
        .sort(
          (a, b) =>
            (a.porcentaje ?? Number.POSITIVE_INFINITY) - (b.porcentaje ?? Number.POSITIVE_INFINITY) || a.id - b.id,
        );
    },
  });
}

const clamp = (v: number) => Math.max(0, Math.min(100, v));
const fmt = (v: number) => `${Math.round(v)}%`;

export function ProgressRing({ value, size = 18, stroke = 2.75, className }: {
  value: number;
  size?: number;
  stroke?: number;
  className?: string;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = clamp(value);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={cn("shrink-0 -rotate-90", className)} aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--primary) / 0.15)" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - v / 100)}
        className="transition-[stroke-dashoffset] duration-500"
      />
    </svg>
  );
}

function EscaleraEtapas({ etapas, idActual, nombreActual, porcentaje }: {
  etapas: EtapaCatalogo[];
  idActual?: number | null;
  nombreActual: string;
  porcentaje: number;
}) {
  let idx = idActual != null ? etapas.findIndex((e) => e.id === idActual) : -1;
  if (idx < 0) idx = etapas.findIndex((e) => e.nombre.trim().toLowerCase() === nombreActual.trim().toLowerCase());

  return (
    <div className="w-[240px] max-w-[calc(100vw-2rem)] py-1">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Etapas de obra</p>
      <ol className="relative">
        {etapas.map((e, i) => {
          const estado =
            idx >= 0
              ? i < idx ? "hecha" : i === idx ? "actual" : "siguiente"
              : e.porcentaje != null && e.porcentaje < porcentaje ? "hecha" : "siguiente";
          const last = i === etapas.length - 1;
          return (
            <li key={e.id} className="relative flex items-start gap-2.5 pb-2 last:pb-0">
              {!last && (
                <span
                  aria-hidden
                  className={cn(
                    "absolute left-[7px] top-[16px] h-[calc(100%-12px)] w-0.5 rounded-full",
                    estado === "hecha" ? "bg-primary" : "bg-border",
                  )}
                />
              )}
              <span
                aria-hidden
                className={cn(
                  "relative z-[1] mt-px flex size-4 shrink-0 items-center justify-center rounded-full border-2",
                  estado === "hecha" && "border-primary bg-primary text-primary-foreground",
                  estado === "actual" && "border-primary bg-background ring-2 ring-primary/25",
                  estado === "siguiente" && "border-muted-foreground/30 bg-background",
                )}
              >
                {estado === "hecha" && <Check className="size-2.5" strokeWidth={3.5} />}
                {estado === "actual" && <span className="size-1.5 rounded-full bg-primary" />}
              </span>
              <span
                className={cn(
                  "flex min-w-0 flex-1 items-baseline justify-between gap-2 text-[12px] leading-4",
                  estado === "actual" && "font-semibold text-foreground",
                  estado === "hecha" && "text-foreground/80",
                  estado === "siguiente" && "text-muted-foreground/70",
                )}
              >
                <span className="truncate">
                  {e.nombre}
                  {estado === "actual" && <span className="sr-only"> (etapa actual)</span>}
                  {estado === "hecha" && <span className="sr-only"> (completada)</span>}
                </span>
                <span className="shrink-0 tabular-nums">{e.porcentaje != null ? fmt(e.porcentaje) : "—"}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export interface EstatusAvanceChipProps {
  /** `estatus_proyecto.nombre` vigente. */
  nombre: string | null | undefined;
  /** `estatus_proyecto.porcentaje_avance` vigente. Sin % se pinta sólo el nombre. */
  porcentaje: number | null | undefined;
  /** `proyectos.id_estatus_proyecto`, para ubicar la etapa en la escalera. */
  idEstatus?: number | null;
  /** Muestra la escalera de etapas en tooltip (default true). */
  showLadder?: boolean;
  /** "chip" (default, con borde) o "inline" (sin borde, para selectores). */
  variant?: "chip" | "inline";
  /** Pinta el anillo de progreso (default true). */
  ring?: boolean;
  className?: string;
}

export function EstatusAvanceChip({
  nombre,
  porcentaje,
  idEstatus,
  showLadder = true,
  variant = "chip",
  ring = true,
  className,
}: EstatusAvanceChipProps) {
  const hasPct = porcentaje != null && Number.isFinite(porcentaje);
  const catalogo = useEstatusProyectoCatalogo(showLadder && hasPct && !!nombre);
  if (!nombre) return null;

  const base = cn(
    "inline-flex max-w-full items-center gap-1.5 rounded-full text-[12px] font-medium",
    variant === "chip" && "border border-border bg-background px-2 py-0.5",
    className,
  );

  if (!hasPct) {
    return (
      <span className={cn(base, "text-foreground")} title={nombre}>
        <span className="truncate">{nombre}</span>
      </span>
    );
  }

  const pct = clamp(porcentaje as number);
  const content = (
    <>
      {ring && <ProgressRing value={pct} />}
      <span className="truncate text-foreground">{nombre}</span>
      <span aria-hidden className="text-muted-foreground">·</span>
      <span className="shrink-0 tabular-nums text-primary">{fmt(pct)}</span>
    </>
  );
  const etapas = catalogo.data ?? [];

  if (!showLadder || etapas.length === 0) {
    return (
      <span className={base} title={`${nombre} · ${fmt(pct)} de avance`}>
        {content}
      </span>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(base, "cursor-help focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring")}
            aria-label={`Estatus: ${nombre}, ${fmt(pct)} de avance. Ver etapas de obra`}
          >
            {content}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="center" className="px-3 py-2">
          <EscaleraEtapas etapas={etapas} idActual={idEstatus} nombreActual={nombre} porcentaje={pct} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default EstatusAvanceChip;
